const config = require('./configs/base');
const Storage = require('./storage');
const Request = require('./request');
const RequestError = require('./requestError');
const OAuth2Client = require('./oauth2client');
const TokenManager = require('./tokenManager');
const RegistrationManager = require('./registrationManager');
const RecoveryManager = require('./recoveryManager');
const Verification = require('./verification');
const deepExtend = require('./utils/deepExtend');
const Logger = require('./utils/logger');
const assert = require('./utils/assert');
const observerFactory = require('./utils/observer');
const snakeToCamel = require('./utils/snakeToCamel');

/**
 *
 * @constructor
 * @param {Object} options
 */
function User(options) {
  const opts = deepExtend({}, config, options);

  this.options = opts;
  // Storage for latest instance of user
  this.currentUser = null;

  // Services initialization
  this.log = new Logger('[spotii-auth.user]');
  this.storage = new Storage(opts.storage);
  this.oauth2client = new OAuth2Client(opts.oauth);
  this.registrationManger = new RegistrationManager(this.storage, opts.registration);
  this.recoveryManger = new RecoveryManager(this.storage, opts.recovery);
  this.tokenManager = new TokenManager(this.oauth2client, this.storage, {
    storageKey: opts.oauth.storageKey,
  });
  this.verification = new Verification(opts.verification);

  // Prepare for making requests with Bearer Authentication
  this.request = Request.createWithBearer(this.tokenManager.getToken.bind(this.tokenManager), {
    baseUrl: opts.profile.baseUrl,
  });
  this.endpoints = opts.profile.endpoints;

  // Create observer for auth status change
  const observerMethods = observerFactory();
  const listen = observerMethods[0];
  const trigger = observerMethods[1];

  this.triggerAuthStatusChange = trigger;
  this.onAuthStatusChange = function(callback) {
    assert.check(callback, { type: 'function', message: 'Callback must be a function' });

    // Call immediately if current status already resolved
    if (this.isResolved) {
      callback(this.currentUser);
    }

    return listen(callback);
  };

  // Try to restore token from the storage
  const self = this;
  this.tokenManager
    .restore()
    .then(function() {
      return self
        .getProfile()
        .then(function(userInfo) {
          // TODO: Trigger AuthStatusCahnge event
          self.isResolved = true;
          self.triggerAuthStatusChange(userInfo);
          return userInfo;
        })
        .catch(e => {
          self.revoke();
          throw e;
        });
    })
    .catch(function() {
      // No valid token available.
      self.triggerAuthStatusChange(self.currentUser);
      self.isResolved = true;
      self.revoke();
    });
}

/**
 *
 */
User.prototype.getToken = function(extraFetchParams = []) {
  const self = this;
  return this.tokenManager
    .getToken(extraFetchParams)
    .then(tokenInfo => {
      const { accessToken } = tokenInfo || {};

      if ((extraFetchParams || []).length === 0) return accessToken;

      const finalTokenInfo = {
        accessToken,
      };
      extraFetchParams.map(param => {
        finalTokenInfo[snakeToCamel(param)] = tokenInfo[param];
        return;
      });
      return finalTokenInfo;
    })
    .catch(function(e) {
      if (self.currentUser) {
        self.currentUser = null;
        self.triggerAuthStatusChange(self.currentUser);
      }
      throw e;
    });
};

/**
 *
 */
User.prototype.login = function(username, password, kwargs = {}) {
  const self = this;
  return this.oauth2client
    .login(username, password, kwargs)
    .then(function(tokenData) {
      self.log(tokenData);
      tokenData.expires_in *= 1000 + Date.now();
      self.tokenManager.set(tokenData);
      return self
        .getProfile()
        .then(function(userInfo) {
          self.setSessionValidityDuration(tokenData, userInfo);
          self.triggerAuthStatusChange(userInfo);
          return userInfo;
        })
        .catch(e => {
          self.revoke();
          throw e;
        });
    })
    .catch(function(error) {
      throw new RequestError(error);
    });
};

/**
 * Will make redirect to logout URL for OAuth Logut Flow if user is logged in.
 *
 * @returns {Boolean} Returns `false` if user is not logged in.
 */
User.prototype.logout = function(returnUrl) {
  if (this.tokenManager.isPopulated) {
    const idHint = this.tokenManager.get('id_token');
    window.location.href = this.oauth2client.buildLogoutUrl(idHint, returnUrl);
    return true;
  }
  return false;
};

/**
 *
 */
User.prototype.revoke = function() {
  const self = this;
  return this.tokenManager.revoke().then(function() {
    self.tokenManager.flush();
    if (self.currentUser) {
      self.currentUser = null;
      self.triggerAuthStatusChange(self.currentUser);
    }
  });
};

/**
 *
 */
User.prototype.loginWithToken = function(token, kwargs = {}) {
  const self = this;

  return (
    this.oauth2client
      .loginWithToken(token, kwargs)
      // Process token data, receive user info, and throw an event of users sign in.
      .then(function(tokenData) {
        self.log(tokenData);
        tokenData.expires_in *= 1000 + Date.now();
        self.tokenManager.set(tokenData);
        return self
          .getProfile()
          .then(function(userInfo) {
            self.setSessionValidityDuration(tokenData, userInfo);
            self.triggerAuthStatusChange(userInfo);
            return userInfo;
          })
          .catch(e => {
            self.revoke();
            throw e;
          });
      })
      .catch(function(error) {
        throw new RequestError(error);
      })
  );
};

/**
 *
 */
User.prototype.getProfile = function() {
  const self = this;
  return this.request('getProfile', {
    url: this.endpoints.profile,
    method: 'GET',
  })
    .then(function(resp) {
      const userInfo = resp.data;
      self.currentUser = self.serialize(userInfo);
      return self.currentUser;
    })
    .catch(function(error) {
      throw new RequestError(error);
    });
};

/**
 * @deprecated ?
 */
User.prototype.updateProfile = function(data) {
  const requiredFields = ['first_name', 'last_name', 'phone_number', 'date_of_birth', 'agreement'];
  const assertation = requiredFields.reduce((obj, field) => {
    const fieldCamel = snakeToCamel(field);
    const type = field === 'agreement' ? 'boolean' : 'string';
    obj[fieldCamel] = { type: type, message: `${fieldCamel} is required` };
    return obj;
  }, {});

  // Perform check of required fields
  assert.check(data, { type: 'object', message: 'provided data must be an object' }, assertation);

  // Prepare data in sent data with proper formatting
  const perparedData = requiredFields.reduce((obj, field) => {
    const fieldCamel = snakeToCamel(field);
    obj[field] = data[fieldCamel];
    return obj;
  }, {});

  const self = this;

  return this.request('updateProfile', {
    url: this.endpoints.profile,
    method: 'PATCH',
    data: perparedData,
  })
    .then(function(resp) {
      const { profile } = resp.data;
      // eslint-disable-next-line camelcase
      const { phone_verification_token_id } = resp.data.phone_verification_token;
      self.currentUser = self.serialize(profile);
      self.triggerAuthStatusChange(self.currentUser);
      return {
        phoneVerificationId: phone_verification_token_id,
        currentUser: self.currentUser,
      };
    })
    .catch(function(error) {
      throw new RequestError(error);
    });
};

//
// --- Password Change ---
//

/**
 *
 */
User.prototype.requestPasswordChange = function(password, confirmPassword) {
  assert.check(password, { type: 'string', message: 'password is required' });
  assert.check(confirmPassword, { type: 'string', message: 'confirmPassword is required' });

  return this.request('requestPasswordChange', {
    url: this.endpoints.setNewPasswordRequest,
    method: 'POST',
    data: {
      new_password: password,
      confirm_new_password: confirmPassword,
    },
  })
    .then(resp => resp.data)
    .catch(function(error) {
      throw new RequestError(error);
    });
};

User.prototype.confirmPasswordChange = function(url, code) {
  assert.check(url, { type: 'string', message: 'url is required' });
  assert.check(code, { type: 'string', message: 'code is required' });

  return this.request('confirmPasswordChange', {
    url: url,
    method: 'PATCH',
    data: { code: code },
  })
    .then(resp => resp.data)
    .catch(function(error) {
      throw new RequestError(error);
    });
};

/**
 *
 */
User.prototype.requestEmailChange = function(email) {
  assert.check(email, { type: 'string', message: 'email is required' });

  return this.request('requestEmailChange', {
    url: this.endpoints.setNewEmailRequest,
    method: 'POST',
    data: {
      new_email: email,
    },
  })
    .then(resp => resp.data)
    .catch(function(error) {
      throw new RequestError(error);
    });
};

//
// --- Email Change ---
//

/**
 *
 */
User.prototype.confirmEmailChange = function(url, code) {
  assert.check(url, { type: 'string', message: 'url is required' });
  assert.check(code, { type: 'string', message: 'code is required' });

  return this.request('confirmEmailChange', {
    url: url,
    method: 'PATCH',
    data: { code: code },
  })
    .then(resp => resp.data)
    .catch(function(error) {
      throw new RequestError(error);
    });
};

User.prototype.verifyEmailChange = function(token) {
  assert.check(token, { type: 'string', message: 'token is required' });
  const url = this.endpoints.setNewEmailVerify.replace('{token}', token);

  return this.request('verifyEmailChange', {
    url: url,
    method: 'PUT',
  })
    .then(resp => resp.data)
    .catch(function(error) {
      throw new RequestError(error);
    });
};

//
// --- Access recovery ---
//

User.prototype.recoveryInit = function(username, kwargs = {}) {
  return this.recoveryManger.init(username, kwargs).catch(function(error) {
    throw new RequestError(error);
  });
};

User.prototype.recoveryConfirm = function(code) {
  return this.recoveryManger.confirmWithCode(code).catch(function(error) {
    throw new RequestError(error);
  });
};

User.prototype.recoveryNewPassword = function(newPassword, confirmNewPassword) {
  return this.recoveryManger.setNewPassword(newPassword, confirmNewPassword).catch(function(error) {
    throw new RequestError(error);
  });
};

//
// --- Email verification ---
//
User.prototype.emailVerification = function(token) {
  const url = this.endpoints.emailVerification.replace('{token}', token);

  return Request.send({
    url: url,
    baseURL: this.options.profile.baseUrl,
    method: 'PUT',
  })
    .then(resp => resp.data)
    .catch(function(error) {
      throw new RequestError(error);
    });
};

User.prototype.resendVerificationEmail = function(token) {
  const url = this.endpoints.resendVerificationEmail.replace('{token}', token);
  return this.request('resendVerificationEmail', {
    url: url,
    method: 'POST',
    data: {
      email: this.currentUser.user.email,
    },
  })
    .then(resp => resp.data)
    .catch(function(error) {
      throw new RequestError(error);
    });
};

// --- Update session validity duration ---
/**
 * Will set the session validity duration for Saudi users to 5 minutes after calling profile me endpoint
 *
 * @method setSessionValidityDuration
 * @returns {Boolean} Returns `false` if user is not logged in.
 */
User.prototype.setSessionValidityDuration = function(tokenData, userInfo) {
  if (
    userInfo &&
    userInfo.user &&
    userInfo.user.phone_number &&
    userInfo.user.phone_number.startsWith('+966')
  ) {
    tokenData.expires_in = this.tokenManager.options.saudiBufferTime + Date.now();
    this.tokenManager.set(tokenData, true);
  } else {
    tokenData.expires_in = this.tokenManager.options.bufferTime + Date.now();
    this.tokenManager.set(tokenData, false);
  }
};

/**
 * Serializer fof data from server into User model
 *
 * @method serialize
 * @param {Object} rawData
 * @returns {Object} Serialized data
 */
User.prototype.serialize = function(rawData) {
  return rawData;
};

module.exports = User;
